import { Minus, Plus } from "lucide-react";

import { Button, Icon } from "@sirdab/ui";

import { formatCount } from "../format/money";
import { useMessages } from "../i18n";

export interface QtyStepperProps {
  value: number;
  min?: number;
  max: number;
  onChange: (next: number) => void;
  /** Already-localised accessible name for the group. */
  label: string;
  disabled?: boolean;
}

/**
 * Quantity for a line where `qty > 1` (docs/08 step 2). Two buttons and a readout rather
 * than a number input: at 390px a stepper is one tap, a keyboard-summoning input is four.
 *
 * The +/- glyphs are symmetric, so nothing here flips in RTL. The count itself is
 * `dir="ltr"` — a bare numeral is a bidi-neutral run.
 */
export function QtyStepper({
  value,
  min = 1,
  max,
  onChange,
  label,
  disabled = false,
}: QtyStepperProps): React.JSX.Element {
  const { locale } = useMessages();
  const clamp = (n: number): number => Math.min(Math.max(n, min), max);

  return (
    <div role="group" aria-label={label} className="flex items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="icon-xl"
        disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - 1))}
        aria-label={`${label} −`}
      >
        <Icon icon={Minus} />
      </Button>
      <output dir="ltr" className="text-body tabular min-w-10 text-center">
        {formatCount(value, locale)}
      </output>
      <Button
        type="button"
        variant="secondary"
        size="icon-xl"
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + 1))}
        aria-label={`${label} +`}
      >
        <Icon icon={Plus} />
      </Button>
    </div>
  );
}
